import { instance } from "api/apiBase";
import { useRef, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import { AccountStatusVariant, SortOrder } from "../constants";
import { getErrorMessage } from "utils/error";

import {
  FilterOptions,
  Renter,
  SubscribersCSVResponse,
  Verification
} from "../types";
import { toast } from "react-toastify";
import { useAuthContext } from "providers/Auth";
import {
  fetchPagesInParallel,
  ProgressUpdate
} from "shared/utils/fetch-pages-in-parallel";

const DEFAULT_PER_PAGE = window.localStorage.getItem("per_page") || "10";

type FilterParams = Record<string, string | string[] | number[] | undefined>;

const query = "portal/v1/customers/all";

// "Report all" is processed on the server in the background. Poll the list
// while the job is draining it, which also keeps the button disabled for as
// long as the work runs. A fixed deadline cannot do that: thousands of renters
// take longer than any deadline short enough to be useful, so give up on lack
// of progress instead, with a hard ceiling as a last resort.
const BULK_REPORT_POLL_MS = 5000;
const BULK_REPORT_STALL_MS = 3 * 60 * 1000;
const BULK_REPORT_MAX_MS = 30 * 60 * 1000;

const buildFilterParams = (options: FilterOptions): FilterParams => {
  const {
    moveInDateFrom,
    moveInDateTo,
    moveOutDateFrom,
    moveOutDateTo,
    leaseStartDateFrom,
    leaseStartDateTo,
    selectedProperties,
    selectedStates,
    selectedEnrollmentStatuses
  } = options;

  const ret: FilterParams = {};
  if (moveInDateFrom) ret.move_in_date_from = moveInDateFrom;
  if (moveInDateTo) ret.move_in_date_to = moveInDateTo;
  if (moveOutDateFrom) ret.move_out_date_from = moveOutDateFrom;
  if (moveOutDateTo) ret.move_out_date_to = moveOutDateTo;
  if (leaseStartDateFrom) ret.lease_start_date_from = leaseStartDateFrom;
  if (leaseStartDateTo) ret.lease_start_date_to = leaseStartDateTo;
  if (selectedProperties && selectedProperties.length > 0)
    ret.property_ids = selectedProperties;
  if (selectedStates && selectedStates.length > 0) ret.states = selectedStates;
  if (selectedEnrollmentStatuses && selectedEnrollmentStatuses.length > 0)
    ret.account_status = selectedEnrollmentStatuses;
  return ret;
};

const buildBaseParams = (options: {
  perPage?: string;
  page?: string;
  search?: string;
  type?: string;
  orderDirection: SortOrder;
  orderBy: string;
}): FilterParams => {
  const ret: FilterParams = {};
  if (options.perPage) ret.per_page = options.perPage;
  if (options.page) ret.page = options.page;
  if (options.search) ret.search = options.search;
  if (options.type !== undefined) ret.type = options.type;
  ret.order_direction = options.orderDirection;
  ret.order_by = options.orderBy;
  return ret;
};

export const useRentReportingController = (options: FilterOptions = {}) => {
  const [sorted, setSorted] = useState<{ key: string; desc: boolean }>({
    key: "",
    desc: true
  });

  const { type } = useAuthContext();

  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      search: "",
      per_page: DEFAULT_PER_PAGE,
      page: "1",
      type: AccountStatusVariant.NeedToReport
    },
    ["per_page", "page", "search", "type"]
  );

  const [debouncedParams] = useDebouncedValue(searchParams.search, 500);

  const getSubscribers = async (
    type?: string,
    filterOptions: FilterOptions = {}
  ): Promise<SubscribersCSVResponse> => {
    let typeParam: string | undefined;

    if (type) typeParam = type;
    else if (searchParams.type === AccountStatusVariant.All)
      typeParam = undefined;
    else typeParam = searchParams.type;

    const params: FilterParams = {
      ...buildBaseParams({
        perPage: searchParams.per_page || DEFAULT_PER_PAGE,
        page: searchParams.page || "1",
        search: debouncedParams || "",
        type: typeParam,
        orderDirection: sorted.desc ? SortOrder.DESC : SortOrder.ASC,
        orderBy: sorted.key || "id"
      }),
      ...buildFilterParams(filterOptions)
    };

    return instance
      .get(query, { params })
      .then(({ data }: { data: SubscribersCSVResponse }) => data);
  };

  const getFilteredSubscribersToCSV = async (
    options: FilterOptions = {},
    onProgress?: (progress: ProgressUpdate) => void
  ): Promise<Renter[]> => {
    const {
      selectedEnrollmentStatuses,
      selectedProperties,
      selectedStates,
      moveInDateFrom,
      moveInDateTo,
      moveOutDateFrom,
      moveOutDateTo,
      leaseStartDateFrom,
      leaseStartDateTo
    } = options;

    const batchSize = 500;

    const baseParams: FilterParams = {
      ...buildBaseParams({
        perPage: batchSize.toString(),
        search: debouncedParams || "",
        type:
          searchParams.type === AccountStatusVariant.All
            ? undefined
            : searchParams.type,
        orderDirection: sorted.desc ? SortOrder.DESC : SortOrder.ASC,
        orderBy: sorted.key || "id"
      }),
      ...buildFilterParams({
        moveInDateFrom,
        moveInDateTo,
        moveOutDateFrom,
        moveOutDateTo,
        leaseStartDateFrom,
        leaseStartDateTo,
        selectedProperties,
        selectedStates,
        selectedEnrollmentStatuses
      })
    };

    try {
      return await fetchPagesInParallel<Renter>({
        fetchPage: async (page) => {
          const { data }: { data: SubscribersCSVResponse } = await instance.get(
            query,
            { params: { ...baseParams, page: page.toString() } }
          );
          return { items: data.items, totalPages: data.page_count };
        },
        onProgress
      });
    } catch {
      throw new Error("Failed to get subscribers for CSV");
    }
  };

  const [bulkReportStartedAt, setBulkReportStartedAt] = useState<number | null>(
    null
  );
  // Last count we saw drop, and when. Lives in a ref because the poll callback
  // reads it on every tick and a re-render per tick would be pointless.
  const bulkProgressRef = useRef<{ left: number; at: number } | null>(null);

  const subscribersMeta = useQuery({
    queryKey: [
      query,
      {
        ...buildBaseParams({
          perPage: searchParams.per_page || DEFAULT_PER_PAGE,
          page: searchParams.page || "1",
          search: debouncedParams || "",
          type: searchParams.type || undefined,
          orderDirection: sorted.desc ? SortOrder.DESC : SortOrder.ASC,
          orderBy: sorted.key || "id"
        }),
        ...buildFilterParams(options)
      }
    ],
    queryFn: () => getSubscribers(undefined, options),
    placeholderData: keepPreviousData,
    enabled: type.isRentReporting || type.isSDK,
    // Evaluated after every fetch, so progress is checked even when the count
    // stops moving (some RVs may stay pending if they fail server-side).
    // The count is scoped to the active search/filters while the job reports
    // partner-wide, so a zero only means "done" when nothing is filtered.
    refetchInterval: (q) => {
      if (bulkReportStartedAt === null) return false;
      const left = q.state.data?.extra?.counts?.need_to_report ?? 0;
      const filtered =
        Boolean(debouncedParams) ||
        Object.keys(buildFilterParams(options)).length > 0;

      const progress = bulkProgressRef.current;
      if (progress === null || left < progress.left)
        bulkProgressRef.current = { left, at: Date.now() };

      const since = bulkProgressRef.current?.at ?? bulkReportStartedAt;
      const stalled = Date.now() - since > BULK_REPORT_STALL_MS;
      const expired = Date.now() - bulkReportStartedAt > BULK_REPORT_MAX_MS;
      if ((left === 0 && !filtered) || stalled || expired) {
        setBulkReportStartedAt(null);
        bulkProgressRef.current = null;
        return false;
      }
      return BULK_REPORT_POLL_MS;
    }
  });

  const refetchAfterReport = () => {
    subscribersMeta.refetch();
  };

  const { mutate: reportSingleRenter, isPending: isReportingSingleRenter } =
    useMutation({
      mutationKey: ["REPORT_SINGLE_RENTER"],
      mutationFn: (data: {
        subscriberId: number;
        verificationId: number;
        verifications: Verification;
      }) =>
        instance.patch(
          `/portal/v1/customers/${data.subscriberId}/verifications/${data.verificationId}`,
          { ...data.verifications }
        ),
      onSuccess: () => {
        toast.success("Renter has been successfully reported");
        refetchAfterReport();
        queryClient.invalidateQueries({ queryKey: ["SUBSCRIBER_DETAIL"] });
      },
      onError: (error) => {
        toast.error(getErrorMessage(error));
      }
    });

  const { mutate: reportAllRenters, isPending: isReportingAllRenters } =
    useMutation({
      mutationKey: ["REPORT_ALL_RENTERS"],
      mutationFn: (data: {
        verifications: Verification[];
        defaultAction: string;
      }) =>
        instance.patch(`/portal/v1/customers`, {
          verifications: data.verifications,
          default_action: data.defaultAction
        }),
      onSuccess: () => {
        toast.success(
          "Reporting started. The list will update as renters are processed."
        );
        setBulkReportStartedAt(Date.now());
        bulkProgressRef.current = null;
        refetchAfterReport();
        setSearchParams({ ...searchParams, page: "1" });
      },
      onError: (error) => {
        toast.error(getErrorMessage(error));
      }
    });

  return {
    subscribersMeta,
    reportSingleRenter,
    searchParams,
    setSearchParams,
    debouncedParams,
    reportAllRenters,
    isReportingSingleRenter,
    isReportingAllRenters,
    isBulkReportInProgress: bulkReportStartedAt !== null,
    sorted,
    setSorted,
    getSubscribers,
    getFilteredSubscribersToCSV
  };
};
